import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';
import { BiBarcodeReader } from 'react-icons/bi';
import { isEmpty } from 'lodash';
import { req } from 'react-reqq-lite';
import { useParams } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';
import { Quagga as QuaggaBarcode } from 'quagga';

import { FormInput } from 'modules/common';
import Scanner from 'modules/merchant/products/product/components/Scanner';
import { useGetCart } from 'modules/branch/services-management/services/hooks';

import { useGetSelectedProducts } from '../hooks';
import * as c from '../constants';

const DEFAULT_FORM = {
  sku: '',
};

function Barcode({ type, branch, onShowBarcode }) {
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const [scannerError, setScannerError] = React.useState('');
  const controller = React.useRef(new AbortController());
  const [showScanner, setShowScanner] = React.useState(false);
  const ref = React.useRef();
  const [isCameraLoading, setIsCameraLoading] = React.useState(false);

  const params = useParams();
  const { merchant_uuid, branch_uuid } = params || {};

  const [isLoading, , refresh] = useGetSelectedProducts({
    params: {
      branch_uuid: branch?.value,
      is_available: 1,
    },
    merchant_uuid,
    branch_uuid,
    qrKey: params?.key,
    type,
  });
  const [, , included] = useGetCart();

  const handleOnDetect = React.useCallback(
    (result, Quagga) => {
      if (!result) return;

      if (Quagga && Quagga?.pause) Quagga?.pause();

      setScannerError('');

      const { signal } = controller.current || {};

      const args = (prevState) => ({
        ...prevState,
        params: {
          ...prevState?.params,
          sku: result,
        },
        signal,
      });

      refresh(args, (res) => {
        let timer;
        if (Quagga && Quagga?.start) {
          timer = setTimeout(() => {
            Quagga?.start();
          }, 1500);
        }

        if (res?.length <= 0 || isEmpty(res)) {
          setScannerError(`Product with sku:${result} does not exist.`);
          return;
        }

        clearTimeout(timer);
        if (Quagga && Quagga?.stop) Quagga.stop();
        setShowScanner(false);

        setForm((prevState) => ({
          ...prevState,
          sku: '',
        }));

        included?.onShowOrderDetails(
          {
            ...res,
            selected_variant: res?.variants?.find(
              (item) => item?.sku === result
            ),
          },
          {
            withSku: result,
          }
        );
      });
    },
    [included, refresh]
  );

  const handleOnStop = (Quagga) => {
    if (Quagga && Quagga?.start) {
      setTimeout(() => {
        Quagga?.start();
      }, 1500);
    }
    controller.current.abort();
    controller.current = new AbortController();
    req.set(c.GET_SELECTED_PRODUCT, false);
  };

  const handleOnClick = () => {
    if (showScanner) {
      setShowScanner(false);
      if (QuaggaBarcode && QuaggaBarcode?.stop) QuaggaBarcode.stop();

      return;
    }

    setShowScanner(true);
  };
  const handleOnAdd = (e) => {
    e.preventDefault();
    handleOnDetect(form?.sku);
  };
  const handleOnChange = (v) => {
    setScannerError('');
    setForm((prevState) => ({
      ...prevState,
      ...v(),
    }));
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      ref.current.focus();
    }, 250);

    return () => clearTimeout(timer);
  }, []);

  React.useEffect(() => {
    onShowBarcode(showScanner);
  }, [showScanner, onShowBarcode]);

  return (
    <div className="flex flex-col">
      {showScanner && (
        <Scanner
          stopOnDetect={false}
          onDetected={handleOnDetect}
          withLightingDescription
          // withPageLoader
          // error={scannerError}
          withSkuPreview
          onStop={handleOnStop}
          isLoading={isLoading}
          setIsCameraLoading={setIsCameraLoading}
          onClose={setShowScanner}
        />
      )}

      <div className="flex items-center gap-2.5 pr-1">
        <form
          noValidate
          onSubmit={handleOnAdd}
          className="flex-1"
          autoComplete="off"
        >
          <FormInput
            name="sku"
            onChange={handleOnChange}
            ref={ref}
            value={form?.sku}
            label={false}
            placeholder="Enter to search SKU"
            trailingItem={
              isLoading && (
                <CgSpinner className="h-5 w-5 text-primary-500 animate-spin mr-0.5" />
              )
            }
            errorClassName="bottom-auto left-1 -mt-3 right-10"
            error={scannerError}
          />
        </form>

        <div
          className={cn('p-1.5 rounded-md cursor-pointer', {
            'bg-primary-500': showScanner,
          })}
          onClick={handleOnClick}
          role="presentation"
        >
          {isCameraLoading ? (
            <CgSpinner className="h-8 w-8 animate-spin" />
          ) : (
            <BiBarcodeReader className="text-white h-8 w-8" />
          )}
        </div>
      </div>
    </div>
  );
}

Barcode.defaultProps = {
  type: 'BRANCH',
  branch: {},
  onShowBarcode: () => {},
};

Barcode.propTypes = {
  type: PropTypes.string,
  branch: PropTypes.instanceOf(Object),
  onShowBarcode: PropTypes.instanceOf(Function),
};

export default Barcode;
